import {
    ButtonPrimary,
    Row,
    RowList,
    ButtonFixedFooterLayout,
    Text6,
    Text3,
    ButtonLink,
    skinVars,
    useDialog,
    IconInformationUserRegular,
    IconLockClosedRegular,
    Circle,
    Text1,
    useWindowSize,
} from '@telefonica/mistica';
import React, { useEffect, useState } from 'react';
import { isBrowser } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { isWebviewAndroid, isWebviewIOS } from '../../../../utils/navigation/controlDevices';
import './index.scss';
import { TrackingEvents } from '../../../../utils/dataTransformation/tracking';
import { getOnboardingWebview, getOnboardingWebview2 } from '../../../../services/EscalationService';
import { AxiosResponse } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { getCustomerData } from '../../../../services/CustomerService';
import { getEnv } from '../../../../utils/dataTransformation/environments';

const CustomerVerification: React.FC = () => {
    const navigate = useNavigate();
    const { trackingScreenView } = new TrackingEvents();
    const { t } = useTranslation();
    const { dialog } = useDialog();
    const size = useWindowSize();
    const params = new URLSearchParams(window.location.search);
    const obd = params.get('functionality');
    const [onboardingUrl, setOnboardingUrl] = useState<string>();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const state = uuidv4();

        const rawTypes = getEnv('VITE_WEBVIEW_OBD_V2_FISCAL_TYPES');
        const v2FiscalTypesObj = rawTypes
            .split(',')
            .map((t) => t.trim().toUpperCase())
            .filter(Boolean)
            .map((t) => t.replace(/_+$/, ''))
            .reduce(
                (acc, token) => {
                    acc[token] = true;
                    return acc;
                },
                {} as Record<string, true>,
            );

        getCustomerData().then((response) => {
            const fiscalIdTypeRaw: string = (response?.data?.fiscalIdType || '').toUpperCase();
            const fiscalIdTypeNorm = fiscalIdTypeRaw.replace(/_+$/, '');

            if (fiscalIdTypeRaw === 'CIF' || fiscalIdTypeRaw === 'CIF_') {
                const feedbackParams = new URLSearchParams();
                feedbackParams.set('feedbackKey', 'cif');
                navigate(`/customer-verification/feedback?${feedbackParams.toString()}`, {
                    state: { feedbackKey: 'cif' },
                });
                return;
            }

            const directMatch = !!v2FiscalTypesObj[fiscalIdTypeNorm];
            const hasPASA = !!v2FiscalTypesObj['PASA'];
            const startsWithPASA = fiscalIdTypeNorm.startsWith('PASA');
            const isV2 = directMatch || (hasPASA && startsWithPASA) || false;

            setLoading(false);

            if (isV2) {
                getOnboardingWebview2(window.location.origin + '/customer-verification/callback/v2', state).then(
                    (response: AxiosResponse) => {
                        setOnboardingUrl(response.data.url);
                        const { data } = response;
                        const { onBoardingId, contactSessionId } = data;
                        localStorage.setItem(
                            'escalation',
                            JSON.stringify({ onBoardingId, contactSessionId, state: { data: data, obd: obd } }),
                        );
                        navigate(`/customer-verification?functionality=${params.get('functionality')}`, {
                            state: { data: data, obd: obd },
                        });
                    },
                );
            } else {
                getOnboardingWebview(window.location.origin + '/customer-verification/callback', state).then(
                    (response: AxiosResponse) => {
                        setOnboardingUrl(response.data.url);
                        const { data } = response;
                        const { onBoardingId, contactSessionId } = data;
                        localStorage.setItem(
                            'escalation',
                            JSON.stringify({ onBoardingId, contactSessionId, state: { data: data, obd: obd } }),
                        );
                        navigate(`/customer-verification?functionality=${params.get('functionality')}`, {
                            state: { data: data, obd: obd },
                        });
                    },
                );
            }
        });
    }, []);

    useEffect(() => {
        if (!(isWebviewAndroid || isWebviewIOS)) {
            trackingScreenView();
        }
    }, []);

    if (loading) return null;

    return (
        <div
            className='onboarding-first-verification-page'
            style={{
                borderLeft: size.width >= 1024 ? `24px solid ${skinVars.colors.backgroundSkeleton}` : 'none',
                borderRight: size.width >= 1024 ? `24px solid ${skinVars.colors.backgroundSkeleton}` : 'none',
            }}
        >
            {isBrowser && (
                <div
                    className='onboarding-first-verification-header-navigationBar'
                    style={{ background: skinVars.colors.navigationBarBackground }}
                ></div>
            )}
            <div className='onboarding-first-verification'>
                <ButtonFixedFooterLayout
                    button={
                        <ButtonPrimary
                            onPress={() =>
                                dialog({
                                    asset: <IconInformationUserRegular color={skinVars.colors.brand} size={64} />,
                                    title: t('verification_important_information'),
                                    message: t('verification_important_information_explain_text1'),
                                    acceptText: t('got_it'),
                                    link: (
                                        <ButtonLink
                                            onPress={() => {
                                                window.open('https://o2online.es/privacidad/', '_blank');
                                            }}
                                        >
                                            {t('visitO2webText')}
                                        </ButtonLink>
                                    ),
                                    onAccept: () => {
                                        window.location.href = onboardingUrl!;
                                    },
                                })
                            }
                        >
                            {t('onboardingFirstVerButton')}
                        </ButtonPrimary>
                    }
                >
                    <div className='onboarding-first-verification__wrapper'>
                        <div className='onboarding-first-verification__wrapper__icon'>
                            <IconLockClosedRegular size={64} color={skinVars.colors.brand}></IconLockClosedRegular>
                        </div>
                        <div className='onboarding-first-verification__wrapper__text1'>
                            <Text6 color={skinVars.colors.textPrimary}>{t('verification_secure')}</Text6>
                        </div>
                        <div className='onboarding-first-verification__wrapper__text2'>
                            <Text3 regular color={skinVars.colors.textSecondary}>
                                {t('verification_secure_explain')}
                            </Text3>
                        </div>
                        <div className='onboarding-first-verification__wrapper__rows'>
                            <RowList>
                                <Row
                                    asset={
                                        <Circle size={24} backgroundColor={skinVars.colors.controlActivated}>
                                            <Text1 medium color={skinVars.colors.textPrimaryInverse}>
                                                1
                                            </Text1>
                                        </Circle>
                                    }
                                    title={t('identity_check')}
                                    description={t('identity_check_explain')}
                                />
                                <Row
                                    asset={
                                        <Circle size={24} backgroundColor={skinVars.colors.controlActivated}>
                                            <Text1 medium color={skinVars.colors.textPrimaryInverse}>
                                                2
                                            </Text1>
                                        </Circle>
                                    }
                                    title={t('contact_media_verification')}
                                    description={t('contact_media_verification_explain')}
                                />
                            </RowList>
                        </div>
                    </div>
                </ButtonFixedFooterLayout>
            </div>
        </div>
    );
};

export default CustomerVerification;
